import {
  Article,
  Construction,
  KeyboardArrowDown,
  KeyboardArrowUp,
  People,
} from "@mui/icons-material";
import { Box, ListItemDecorator, Stack, TabList, Tabs, Typography } from "@mui/joy";
import { Outlet, useMatchRoute } from "@tanstack/react-router";
import { ReactNode, useState } from "react";

import { useChallengeQuery } from "@/hooks/challenges";
import { useCurrentVenueQuery, useCurrentVenueUuid } from "@/hooks/venues";
import formatPeriod from "@/utils/formatPeriod";

import { TabLink } from "../Links";
import ChallengesList from "./ChallengesList";

const ChallengeOverview = ({
  challengeUuid,
  initialTab = "profile",
  children,
}: {
  challengeUuid?: string;
  initialTab?: "profile" | "entries" | "tools";
  children?: ReactNode;
}) => {
  const currentVenueUuid = useCurrentVenueUuid();

  const { data: venue } = useCurrentVenueQuery();

  const { data: challenge } = useChallengeQuery(challengeUuid, currentVenueUuid);

  let activeTab = initialTab;

  const matchRoute = useMatchRoute();
  if (
    matchRoute({
      to: "/gym/$venueUuid/challenges/$challengeUuid/entries",
      fuzzy: true,
    })
  ) {
    activeTab = "entries";
  }

  if (
    matchRoute({
      to: "/gym/$venueUuid/challenges/$challengeUuid/tools",
      fuzzy: true,
    })
  ) {
    activeTab = "tools";
  }

  const [competitionSelectorOpen, setCompetitionSelectorOpen] = useState(activeTab === "profile");

  // oxlint-disable-next-line prefer-const
  // let { competitionSelectorOpen, setCompetitionSelectorOpen } =
  //   useCompetitionContext();

  // const isCreate = !!matchRoute({
  //   to: "/gym/$venueUuid/challenges/create",
  //   fuzzy: true,
  // });
  const isCreate = !!matchRoute({
    to: "/gym/$venueUuid/challenges/create",
    fuzzy: true,
  });
  const canCloseSelector = true;

  return (
    <Box
      sx={{
        display: "flex",
        height: "100dvh",
        flexDirection: "column",
      }}
    >
      <Tabs value={activeTab}>
        <TabList
          sx={{
            flex: 1,
            ".MuiTab-root": {
              alignSelf: "flex-end",
            },
          }}
        >
          {isCreate || challenge ? (
            <Box
              sx={{
                width: 350,
                backgroundColor: "neutral.100",
                marginX: 1,
                marginY: 0.5,
                display: "flex",
                alignItems: "center",
                border: "1px solid",
                borderColor: "neutral.200",
                borderRadius: 8,
                paddingX: 1,
                cursor: "pointer",
                overflow: "hidden",
              }}
              onClick={() => {
                setCompetitionSelectorOpen(!competitionSelectorOpen);
              }}
            >
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="baseline">
                  <Typography
                    level="body-lg"
                    fontWeight="bold"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {challenge ? challenge.name : "Challenges"}
                  </Typography>
                </Stack>
                <Stack direction="row">
                  <Typography level="body-sm" color="neutral">
                    {challenge ? formatPeriod(challenge, venue?.timezone || "UTC") : "Upcoming"}
                  </Typography>
                </Stack>
              </Stack>
              {!competitionSelectorOpen ? (
                <KeyboardArrowDown sx={{ marginRight: -0.75 }} />
              ) : canCloseSelector ? (
                <KeyboardArrowUp sx={{ marginRight: -0.75 }} />
              ) : null}
            </Box>
          ) : (
            <Box
              sx={{
                width: 350,
                marginX: 1,
                marginY: 0.5,
                display: "flex",
                alignItems: "center",
                borderRadius: 8,
                paddingX: 1,
                cursor: "pointer",
                overflow: "hidden",
              }}
            >
              <Typography level="body-lg" fontWeight="bold">
                Events
              </Typography>
            </Box>
          )}
          <TabLink
            value="profile"
            sx={{ flex: 1, height: 40 }}
            from="/gym/$venueUuid"
            to={
              challengeUuid
                ? "/gym/$venueUuid/challenges/$challengeUuid/profile"
                : "/gym/$venueUuid/challenges/create"
            }
            params={(prev) => ({ ...prev, challengeUuid })}
            replace
          >
            <ListItemDecorator>
              <Article />
            </ListItemDecorator>
            Profile
          </TabLink>
          <TabLink
            value="entries"
            sx={{ flex: 1, height: 40 }}
            from="/gym/$venueUuid/challenges/$challengeUuid"
            to="./entries"
            replace
            disabled={!challengeUuid}
          >
            <ListItemDecorator>
              <People />
            </ListItemDecorator>
            Entries
          </TabLink>
          <TabLink
            value="tools"
            sx={{ flex: 1, height: 40 }}
            from="/gym/$venueUuid/challenges/$challengeUuid"
            to="./tools"
            replace
            disabled={!challengeUuid}
          >
            <ListItemDecorator>
              <Construction />
            </ListItemDecorator>
            Tools
          </TabLink>
        </TabList>
      </Tabs>
      <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
        {competitionSelectorOpen && (
          <ChallengesList activeChallengeUuid={challengeUuid} isCreating={isCreate} />
        )}
        <Box sx={{ flex: 1, display: "flex", minWidth: 0 }}>{children || <Outlet />}</Box>
      </Stack>
    </Box>
  );
};

export default ChallengeOverview;
